import React, { useEffect } from 'react';

import { SidePage } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import Permissions from '@gogame/permissions';

import {
  ACTION_SHARE,
  ACTION_ADD_LOOKALIKE_AUDIENCES,
} from 'scenes/CustomAudiences/PartnerAudiences/constants';
import {
  useIsUpdating,
  useLoadPADetails,
  useLoadSharedaccountsPA,
} from 'scenes/CustomAudiences/PartnerAudiences/data';
import { useLoadLookalikeAudiencesPA } from 'scenes/CustomAudiences/PartnerAudiences/data';
import { TAB_DETAILS, TAB_ACTIONS_LOG } from 'scenes/CustomAudiences/constants';

import PAActions from '../Actions';
import { PartnerAudienceFormEdit } from '../PartnerAudienceForm';
import ActionsLog from './ActionsLog';
import GGAudiences from './GGAudiences';
import Lookalikes from './LookalikeAudiences';
import General from './Sections/General';
import MetaData from './Sections/MetaData';
import SharedAccounts from './SharedAccounts';
import ReceiveNotificationSwitcher from './ReceiveNotificationSwitcher';

const PartnerAudienceDetails = ({
  id,
  onClose,
  onExited,
  open,
  activeTabId = TAB_DETAILS,
}) => {
  const isUpdating = useIsUpdating();
  const { data, isLoading, isRefetching } = useLoadPADetails(id);
  const {
    data: lookalikesData,
    isLoading: isLoadingLookalikes,
    refetch: refetchLookalikes,
  } = useLoadLookalikeAudiencesPA({
    partnerAudience: id,
  });
  const { data: sharedAccountsData, isLoading: isLoadingSharedAccounts } =
    useLoadSharedaccountsPA({ partnerAudience: id });
  const [childComponent, openChildComponent] = useChildComponent();
  const handleEdit = async ({ id }) => {
    openChildComponent(PartnerAudienceFormEdit, {
      id,
    });
  };

  useEffect(() => {
    refetchLookalikes();
  }, [data?.filterStatus.id]);

  return (
    <React.Fragment>
      <Permissions actions={data?.actions}>
        <SidePage
          onClose={onClose}
          onExited={onExited}
          open={open}
          loading={isLoading || isUpdating}
          fetching={
            isRefetching || isLoadingLookalikes || isLoadingSharedAccounts
          }
          backdrop={false}
          size={'lg'}
        >
          <SidePage.Title
            title={'Partner Audience'}
            subTitle={data?.name}
            entityId={id}
            actions={
              <ReceiveNotificationSwitcher
                id={data?.id}
                value={data?.receiveNotification}
              />
            }
          />
          <SidePage.Tabs activeTabId={activeTabId}>
            <SidePage.Tab label={TAB_DETAILS}>
              <SidePage.NewBody wrapper={true} dense={true}>
                <SidePage.NewContent>
                  <General data={data} />

                  <Permissions.Action id={ACTION_SHARE}>
                    <SharedAccounts
                      data={sharedAccountsData}
                      partnerAudience={id}
                    />
                  </Permissions.Action>

                  {data?.parentAudience && <GGAudiences data={data} />}

                  <Permissions.Action id={ACTION_ADD_LOOKALIKE_AUDIENCES}>
                    <Lookalikes
                      partnerAudience={id}
                      partner={data?.partner.id}
                      data={lookalikesData}
                    />
                  </Permissions.Action>
                </SidePage.NewContent>
                <SidePage.Sidebar
                  actions={
                    <PAActions
                      actions={data?.actions}
                      data={data}
                      isAllActionsAvailable={false}
                      limit={3}
                      onClose={onClose}
                      onEdit={handleEdit}
                    />
                  }
                >
                  <MetaData data={data} />
                </SidePage.Sidebar>
                {childComponent}
              </SidePage.NewBody>
            </SidePage.Tab>
            <SidePage.Tab label={TAB_ACTIONS_LOG}>
              <SidePage.NewBody scroll={false}>
                <ActionsLog id={id} />
              </SidePage.NewBody>
            </SidePage.Tab>
          </SidePage.Tabs>
          <SidePage.Actions>
            <SidePage.CloseButton />
          </SidePage.Actions>
        </SidePage>
      </Permissions>
    </React.Fragment>
  );
};
export default PartnerAudienceDetails;
